/**
 * CIRCLE / ARC / ELLIPSE serializers. Angles are written in degrees (DXF
 * group 50/51); ellipse start/end stay in parameter radians (41/42) as the
 * format requires.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';

const deg = (rad: number): number => ((rad * 180) / Math.PI + 360) % 360;

export function writeCircle(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, unknown>;
    const c = d.center as number[];
    if (!Array.isArray(c)) return;
    entityPrologue(w, ctx, e, 'CIRCLE', owner);
    w.pair(100, 'AcDbCircle');
    w.pair(10, c[0] ?? 0).pair(20, c[1] ?? 0).pair(30, c[2] ?? 0);
    w.pair(40, typeof d.radius === 'number' ? d.radius : 0);
}

export function writeArc(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, unknown>;
    const c = d.center as number[];
    if (!Array.isArray(c)) return;
    entityPrologue(w, ctx, e, 'ARC', owner);
    w.pair(100, 'AcDbArc');
    w.pair(10, c[0] ?? 0).pair(20, c[1] ?? 0).pair(30, c[2] ?? 0);
    w.pair(40, typeof d.radius === 'number' ? d.radius : 0);
    w.pair(50, deg(typeof d.startAngle === 'number' ? d.startAngle : 0));
    w.pair(51, deg(typeof d.endAngle === 'number' ? d.endAngle : 0));
}

export function writeEllipse(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, unknown>;
    const c = d.center as number[];
    if (!Array.isArray(c)) return;
    const ra = typeof d.radiusA === 'number' ? d.radiusA : 1;
    const rb = typeof d.radiusB === 'number' ? d.radiusB : 1;
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;
    entityPrologue(w, ctx, e, 'ELLIPSE', owner);
    w.pair(100, 'AcDbEllipse');
    w.pair(10, c[0] ?? 0).pair(20, c[1] ?? 0).pair(30, c[2] ?? 0);
    // Major-axis endpoint as a rotation-aware vector + axis ratio.
    w.pair(11, Math.cos(rot) * ra).pair(21, Math.sin(rot) * ra).pair(31, 0);
    w.pair(40, ra !== 0 ? rb / ra : 1);
    // Partial ellipses carry their parameter window; full ellipse = 0..2π.
    w.pair(41, typeof d.startAngle === 'number' ? d.startAngle : 0);
    w.pair(42, typeof d.endAngle === 'number' ? d.endAngle : Math.PI * 2);
}
